import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { UserFeedbackService } from "src/app/Services/user-feedback.service";
import { FormProtectionService } from "src/app/Services/form-protection.service";
import { HttpClient, HttpEventType } from "@angular/common/http";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-send-feedback",
  templateUrl: "./send-feedback.component.html",
  styleUrls: ["./send-feedback.component.scss"],
})
export class SendFeedbackComponent implements OnInit {
  feedbackForm: FormGroup;
  userData: any;
  isSubmitting = false;
  showRecaptchaNotice = false;
  uploadedImages: string[] = [];
  isUploadingImage = false;
  imageUploadProgress = 0;
  private readonly formLoadedAt = Date.now();
  feedbackTypes = [
    { value: "Bug", label: "Bug Report" },
    { value: "Feature Request", label: "Feature Request" },
    { value: "General Feedback", label: "General Feedback" },
    { value: "Performance Issue", label: "Performance Issue" },
    { value: "UI/UX Suggestion", label: "UI/UX Suggestion" },
    { value: "Other", label: "Other" },
  ];

  constructor(
    private fb: FormBuilder,
    private userFeedbackService: UserFeedbackService,
    public router: Router,
    private toast: ToastrService,
    private http: HttpClient,
    private formProtection: FormProtectionService
  ) {
    this.feedbackForm = this.fb.group({
      feedbackType: ["", Validators.required],
      subject: ["", [Validators.required, Validators.maxLength(200)]],
      message: [
        "",
        [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(2000),
        ],
      ],
      rating: [null],
      website: [""],
    });
  }

  ngOnInit(): void {
    this.userData = JSON.parse(localStorage.getItem("LoggedInUser") || "{}");

    this.formProtection.isRecaptchaEnabled().then((enabled) => {
      this.showRecaptchaNotice = enabled;
    });
  }

  async onSubmit() {
    if (!this.feedbackForm.valid || this.isSubmitting) {
      Object.keys(this.feedbackForm.controls).forEach((key) => {
        this.feedbackForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.isSubmitting = true;

    try {
      const recaptchaToken = await this.formProtection.getRecaptchaToken(
        "send_feedback"
      );

      const feedbackData: Record<string, unknown> = {
        feedbackType: this.feedbackForm.value.feedbackType,
        subject: this.feedbackForm.value.subject,
        message: this.feedbackForm.value.message,
        rating: this.feedbackForm.value.rating || null,
        userEmail: this.userData?.emailAddress || null,
        userName: this.userData?.firstName
          ? `${this.userData.firstName} ${this.userData.lastName || ""}`.trim()
          : null,
        imageUrls:
          this.uploadedImages.length > 0 ? this.uploadedImages.join(",") : null,
        Website: this.feedbackForm.value.website || "",
        FormLoadedAt: this.formLoadedAt,
      };

      if (recaptchaToken) {
        feedbackData.RecaptchaToken = recaptchaToken;
      }

      this.userFeedbackService.sendFeedback(feedbackData).subscribe({
        next: () => {
          this.toast.success(
            "Thank you for your feedback! We appreciate your input."
          );
          this.feedbackForm.reset();
          this.uploadedImages = [];
          this.isSubmitting = false;
          setTimeout(() => {
            this.router.navigate(["/"]);
          }, 2000);
        },
        error: () => {
          this.toast.error("Failed to submit feedback. Please try again.");
          this.isSubmitting = false;
        },
      });
    } catch {
      this.toast.error("Failed to submit feedback. Please try again.");
      this.isSubmitting = false;
    }
  }

  getErrorMessage(fieldName: string): string {
    const control = this.feedbackForm.get(fieldName);
    if (control?.hasError("required")) {
      return `${fieldName} is required`;
    }
    if (control?.hasError("minlength")) {
      return `${fieldName} must be at least ${control.errors?.["minlength"].requiredLength} characters`;
    }
    if (control?.hasError("maxlength")) {
      return `${fieldName} must not exceed ${control.errors?.["maxlength"].requiredLength} characters`;
    }
    return "";
  }

  onImageSelect(event: any) {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const remainingSlots = 5 - this.uploadedImages.length;
    const filesToUpload = Array.from(files).slice(0, remainingSlots) as File[];

    if (files.length > remainingSlots) {
      this.toast.warning(
        `You can upload up to 5 images. Only ${remainingSlots} will be uploaded.`
      );
    }

    filesToUpload.forEach((file) => {
      this.uploadImage(file);
    });
  }

  uploadImage(file: File) {
    const imageExtensions = ["jpeg", "jpg", "png", "gif", "webp", "bmp"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (!fileExtension || !imageExtensions.includes(fileExtension)) {
      this.toast.error(
        "Please upload a valid image file (JPEG, PNG, GIF, WEBP, BMP)"
      );
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      this.toast.error("Image size must be less than 10MB");
      return;
    }

    this.isUploadingImage = true;
    this.imageUploadProgress = 0;

    const formData = new FormData();
    formData.append("fileData", file);

    const uploadUrl = environment.apiBaseUrl + environment.uploadImageUrl;

    this.http
      .post(uploadUrl, formData, {
        reportProgress: true,
        observe: "events",
      })
      .subscribe(
        (event: any) => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              if (event.total) {
                this.imageUploadProgress = Math.round(
                  (event.loaded / event.total) * 100
                );
              }
              break;
            case HttpEventType.Response:
              const response = event.body;
              if (response && response.fileName) {
                const imageUrl =
                  environment.imageStorageBaseUrl + response.fileName;
                this.uploadedImages.push(imageUrl);
                this.toast.success("Image uploaded successfully");
                this.isUploadingImage = false;
                this.imageUploadProgress = 0;
              } else {
                this.toast.error("Failed to upload image");
                this.isUploadingImage = false;
                this.imageUploadProgress = 0;
              }
              break;
          }
        },
        () => {
          this.toast.error("Failed to upload image. Please try again.");
          this.isUploadingImage = false;
          this.imageUploadProgress = 0;
        }
      );
  }

  removeImage(index: number) {
    this.uploadedImages.splice(index, 1);
  }
}
